import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Carousel } from 'antd';
import { shuffle } from 'lodash';
import Cookies from 'js-cookie';
import styles from './AdverNew.module.less';

interface AdverProps {
  slot: string | Array<string>;
  width?: number;
  height?: number;
  style?: object;
  className?: any;
  hide?: boolean;
  bg?: boolean;
  setTarget?: Function;
  containerClass?: any;
  handleClick?: Function;
  atarget?: string;
}

interface DataType {
  link?: string;
  img?: string;
}

interface SlotArrType {
  id?: string | number;
  content?: any;
}

/**
 * @description 广告组件，支持banner、tab、carousel模式，多个广告轮播暂时只支持a + img的方式
 * @param {string | string[]} slot 必传，广告id, 单个为banner或tab模式，数组为carousel模式
 * @param {number} width 可选，宽度
 * @param {number} height 可选，高度
 * @param {object} style 可选，自定义样式
 * @param {any} className 可选，自定义类名，支持cssModule
 * @param {boolean} hide 可选，容器显示|隐藏，针对tab模式
 * @param {boolean} bg 可选，采用a + bgi的方式渲染广告内容
 * @param {Function} setTarget 可选，定义一个函数，接收googletag.pubads()，可用于设置键值等附加操作
 * @param {any} containerClass 可选，父容器类名
 * @param {Function} handleClick 可选，父容器点击事件
 * @param {string} atarget 可选，a标签的target属性
 */
export default function Adver({
  slot: adSlot,
  width,
  height,
  style,
  className,
  hide,
  bg,
  setTarget,
  containerClass,
  handleClick,
  atarget = '_blank',
}: AdverProps) {
  // 单个广告的数据
  const [data, setData] = useState<DataType>({});
  // 多个广告的数据
  const [slotArr, setSlotArr] = useState<Array<SlotArrType>>([]);
  const [count, setCount] = useState<number>(0);
  const [isAdEmpty, setIsAdEmpty] = useState(false);
  // 广告id映射表
  const slotMap: object = {};
  // 轮播广告的页码
  let index = 0;
  // 广告插槽实例
  const adSlotRef: any = useRef<HTMLDivElement>(null);
  // 如果能取到广告的数据，则采用a + img的模式渲染，隐藏iframe
  const isHideIframe = useMemo(() => Object.keys(data).length > 0, [data]);
  // 容器尺寸
  const size = useMemo(() => (width && height ? { width: `${width}px`, height: `${height}px` } : {}), [width, height]);
  // 最新的轮播广告数据数组
  const curSlots: any = useRef();
  // 容器點擊事件
  const containerClick = useCallback(() => {
    handleClick && handleClick();
  }, [handleClick]);
  // 广告注入广告位后触发
  const slotRenderEnded = useCallback((event: any) => {
    const { isEmpty, slot } = event;
    // 当前广告的id
    const slotId = slot.getSlotElementId();
    // 将当前广告存入id映射表
    slotMap[slotId] = slot;
    // 如果广告id是一个数组
    const isArr = Array.isArray(adSlot);
    // 则判断当前广告是否在这个数组内（即是否为轮播模式），並且，当前广告的数据是否还未被存到用于轮播的广告数据数组
    // 如果是单个广告，则当前广告id要和每个组件一一对应
    const isCurSlot = isArr
      ? adSlot.includes(slotId) && !curSlots.current.find((item: any) => item.id === slotId)
      : slotId === adSlot;
    if (!isCurSlot) {
      return;
    }
    setIsAdEmpty(isEmpty);

    // 如果广告内容为空，则忽略
    if (isEmpty) {
      // 记录数据处理进度
      setCount((count) => count + 1);
      return;
    }
    // 如果条件成立，通过正则取出广告的内容，包括链接和图片
    // 需要特定格式的广告HTML
    const slotHtml: string = slot.getHtml();
    const slotStr: RegExpMatchArray | null = slotHtml.match(/---(.*?)---/g);

    if (slotStr !== null) {
      // 采用 img + a 方式渲染，否则采用原生iframe渲染
      try {
        const slotContent: object = JSON.parse(slotStr[0].replace(/---/g, ''));
        if (isArr) {
          setSlotArr((slotArr) => shuffle([...slotArr, { id: slotId, content: slotContent }]));
        } else {
          setData(slotContent);
        }
      } catch (err) {}
    } else {
      setData({});
    }
    setCount((count) => count + 1);
  }, []);
  // 初始化广告
  const initAdver = useCallback(() => {
    const { googletag } = window;
    googletag.cmd.push(() => {
      const pubads: any = googletag.pubads();
      pubads.addEventListener('slotRenderEnded', slotRenderEnded);
      if (Array.isArray(adSlot)) {
        adSlot.forEach((item) => {
          googletag.defineSlot(item, [['fluid'], [width, height]], item).addService?.(pubads);
        });
      } else {
        googletag.defineSlot(adSlot, [['fluid'], [width, height]], adSlot).addService?.(pubads);
      }
      pubads.enableSingleRequest();
      googletag.enableServices();
      pubads.collapseEmptyDivs();

      if (Cookies.get('appversion') == 'debug' || Cookies.get('adtest') == 'true') {
        window.googletag.pubads().setTargeting('debug', '1');
      }

      let buycar_stage = localStorage.getItem('buycar_stage');
      window.googletag.pubads().setTargeting('buycar_stage', buycar_stage);

      // 设置键值等附加操作
      setTarget && setTarget(pubads);

      if (Array.isArray(adSlot)) {
        adSlot.forEach((item) => {
          googletag.display(item);
        });
      } else {
        googletag.display(adSlot);
      }
    });
  }, [adSlot, width, height]);
  // 刷新广告
  let refreshAdver = useCallback(() => {
    const { googletag } = window;
    googletag.cmd.push(() => {
      if (Array.isArray(adSlot)) {
        const curSlot: object | undefined = curSlots.current[index];
        if (curSlot) {
          googletag.pubads().refresh([slotMap[curSlot['id']]]);
        }
        return;
      }
      googletag.pubads().refresh([slotMap[adSlot]]);
    });
  }, [adSlot, curSlots]);
  // 切换轮播回调
  const slotsChange = useCallback((idx: number) => {
    // console.log(idx)
    index = idx;
    // 当轮播图的3/4在可视区域内，每轮播一页，就刷新一次曝光
    const { height, top } = adSlotRef.current.getBoundingClientRect();
    const innerHeight = window.innerHeight;
    if (top + (height * 2) / 3 > 0 && top + (height * 2) / 3 < innerHeight) {
      refreshAdver();
    }
  }, []);
  // 初始化
  useEffect(() => {
    initAdver();
    // console.log(data)
    // 监听广告的3/4进入可视区域，刷新曝光
    new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          refreshAdver();
        }
      },
      { threshold: 0.75 },
    ).observe(adSlotRef.current);
  }, []);
  // 监听最新的轮播广告数组
  useEffect(() => {
    curSlots.current = slotArr;
  }, [slotArr]);

  return (
    // tofix：hide修改为控制外部容器，这样切换显示的时候会触发IntersectionObserver监听，就不需要再单独去监听hide手动刷新，也不会导致隐藏的也同时被监听
    <div ref={adSlotRef} className={classNames({ [styles['hide']]: hide }, containerClass)} onClick={containerClick}>
      {!Array.isArray(adSlot) ? (
        <>
          {isHideIframe && (
            <a
              href={data.link}
              target={atarget}
              className={classNames(className, { [styles['bg']]: bg })}
              style={{ ...style, ...(bg ? { backgroundImage: `url(${data.img})` } : {}) }}
            >
              {!bg && <img src={data.img} style={size} alt="" />}
            </a>
          )}
          <div
            id={adSlot}
            className={classNames(className, styles['adver-container'], {
              [styles['hide']]: isAdEmpty || isHideIframe,
            })}
            ref={adSlotRef}
            style={{ ...size, ...style }}
          />
        </>
      ) : (
        <>
          {
            // 数据处理完成后统一渲染，不然自动轮播会失效
            adSlot.length === count && (
              <Carousel autoplay={true} autoplaySpeed={5000} infinite afterChange={slotsChange}>
                {slotArr.map((item) => (
                  <a
                    style={{ display: 'inline-block', ...size }}
                    key={item.id}
                    href={item.content.link}
                    target={atarget}
                  >
                    <img src={item.content.img} style={{ ...size, ...style }} />
                  </a>
                ))}
              </Carousel>
            )
          }
          {adSlot.map((item) => (
            <div id={item} className={styles['hide']} key={item} />
          ))}
        </>
      )}
    </div>
  );
}
